// The host adapter (decision 064): the one application runs in the browser
// and inside the companion's Tauri shell. Under the shell, `detectHost`
// finds Tauri's bridge and answers a `Host` whose methods are the commands
// of the Rust side (`apps/companion/src-tauri/src/commands.rs`); in the
// browser it answers null and the local parts of the pages stay out.

import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { Menu } from "@tauri-apps/api/menu";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { disable, enable, isEnabled } from "@tauri-apps/plugin-autostart";
import { createContext, useContext } from "react";

import type {
  AppInfo,
  CheckoutOutcome,
  Compat,
  InstallPluginOutcome,
  LeaseSummary,
  LiverySummary,
  MaintainOutcome,
  OutboxView,
  PublishInput,
  PublishOutcome,
  SettingsPatch,
  StatusRow,
  UpdateCheck,
  UpdateInfo,
  UploadRecord,
  UserRef,
  VersionSummary, PluginCheck } from "./types";

/** Stops a subscription made with `Host.listen`. */
export type Unlisten = () => void;

/** What the companion's shell gives the pages: the local commands, typed, and its events. */
export interface Host {
  appInfo(): Promise<AppInfo>;
  /** The platform token the shell holds (the credential store), for the pages' own calls to the platform. */
  sessionToken(): Promise<string | null>;
  settingsUpdate(patch: SettingsPatch): Promise<void>;
  /** Whether the companion starts with Windows (RF-04), and setting it. */
  autostartEnabled(): Promise<boolean>;
  setAutostart(on: boolean): Promise<void>;
  /** Fetches the newest plugin from the platform and installs it into every X-Plane found, X-Plane closed (decisions 065 and 067). */
  installPlugin(): Promise<InstallPluginOutcome>;
  /** Reads the platform's plugin pointer now and says what is ready and whether X-Plane holds it (decision 067). */
  pluginCheck(): Promise<PluginCheck>;
  updateCheck(): Promise<UpdateCheck>;
  /** Downloads and verifies what the check found, kept for the install (F489). */
  updateDownload(): Promise<UpdateInfo>;
  /** Installs the update downloaded: the companion closes and the installer takes over. */
  updateInstall(): Promise<string>;
  whoami(): Promise<UserRef | null>;
  /** Opens the Discord login in the browser; the deep link completes it, or `loginComplete` with the pasted link. */
  loginStart(): Promise<string>;
  loginComplete(input: string): Promise<UserRef>;
  logout(): Promise<void>;
  /** The right-click menu under the shell (2026-11-03): Refresh alone, the browser's own menu hidden. */
  contextMenu(): Promise<void>;
  status(): Promise<StatusRow[]>;
  checkout(hsUuid: string, setLastUsed: boolean): Promise<CheckoutOutcome>;
  release(hsUuid: string): Promise<LeaseSummary>;
  publish(hsUuid: string, input: PublishInput): Promise<PublishOutcome>;
  shareLivery(hsUuid: string, rightsConfirmed: boolean): Promise<LiverySummary>;
  maintain(hsUuid: string, action: string): Promise<MaintainOutcome>;
  openUrl(url: string): Promise<void>;
  compatRefresh(): Promise<Compat | null>;
  flyStart(hsUuid: string): Promise<void>;
  watchStart(hsUuid: string): Promise<void>;
  flyStop(): Promise<string | null>;
  versions(hsUuid: string): Promise<VersionSummary[]>;
  uploads(hsUuid: string): Promise<UploadRecord[]>;
  outbox(): Promise<OutboxView>;
  logsTail(lines: number): Promise<string[]>;
  exportDiagnostics(): Promise<string>;
  openPath(path: string): Promise<void>;
  /** The shell's own window in the appearance chosen (F467, decision 090): light, dark, or null to follow the system. */
  setWindowTheme(theme: "light" | "dark" | null): Promise<void>;
  /** The shell's channels: `session`, `transfer`, `login`, `login-error`. */
  listen<T>(channel: string, handler: (payload: T) => void): Promise<Unlisten>;
}

/** The host over Tauri's bridge: one `invoke` per command, `snake_case` on the Rust side, `camelCase` here. */
/** The right-click menu (2026-11-03): Refresh alone, with its shortcut; WebView2's own menu (Back, Save as, Print, More tools) stays hidden by the page. */
async function refreshMenu(): Promise<void> {
  const menu = await Menu.new({
    items: [{ id: "refresh", text: "Refresh", accelerator: "CmdOrCtrl+R", action: () => window.location.reload() }],
  });
  await menu.popup();
}

export function tauriHost(): Host {
  return {
    appInfo: () => invoke<AppInfo>("app_info"),
    sessionToken: () => invoke<string | null>("session_token"),
    settingsUpdate: (patch) => invoke<void>("settings_update", { patch }),
    installPlugin: () => invoke<InstallPluginOutcome>("install_plugin"),
    pluginCheck: () => invoke<PluginCheck>("plugin_check"),
    autostartEnabled: () => isEnabled(),
    setAutostart: (on) => (on ? enable() : disable()),
    updateCheck: () => invoke<UpdateCheck>("update_check"),
    updateDownload: () => invoke<UpdateInfo>("update_download"),
    updateInstall: () => invoke<string>("update_install"),
    whoami: () => invoke<UserRef | null>("whoami"),
    loginStart: () => invoke<string>("login_start"),
    loginComplete: (input) => invoke<UserRef>("login_complete", { input }),
    logout: () => invoke<void>("logout"),
    contextMenu: () => refreshMenu(),
    status: () => invoke<StatusRow[]>("status"),
    checkout: (hsUuid, setLastUsed) => invoke<CheckoutOutcome>("checkout", { hsUuid, setLastUsed }),
    release: (hsUuid) => invoke<LeaseSummary>("release", { hsUuid }),
    publish: (hsUuid, input) => invoke<PublishOutcome>("publish", { hsUuid, input }),
    shareLivery: (hsUuid, rightsConfirmed) => invoke<LiverySummary>("share_livery", { hsUuid, rightsConfirmed }),
    maintain: (hsUuid, action) => invoke<MaintainOutcome>("maintain", { hsUuid, action }),
    openUrl: (url) => invoke<void>("open_url", { url }),
    compatRefresh: () => invoke<Compat | null>("compat_refresh"),
    flyStart: (hsUuid) => invoke<void>("fly_start", { hsUuid }),
    watchStart: (hsUuid) => invoke<void>("watch_start", { hsUuid }),
    flyStop: () => invoke<string | null>("fly_stop"),
    versions: (hsUuid) => invoke<VersionSummary[]>("versions", { hsUuid }),
    uploads: (hsUuid) => invoke<UploadRecord[]>("uploads", { hsUuid }),
    outbox: () => invoke<OutboxView>("outbox"),
    logsTail: (lines) => invoke<string[]>("logs_tail", { lines }),
    exportDiagnostics: () => invoke<string>("export_diagnostics"),
    openPath: (path) => invoke<void>("open_path", { path }),
    setWindowTheme: (theme) => getCurrentWindow().setTheme(theme),
    listen: async (channel, handler) => listen(channel, (e) => handler(e.payload as never)),
  };
}

/** True inside the companion's shell: Tauri puts its bridge on the window before any script runs. */
export function hostPresent(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** The host of this page: Tauri's inside the shell, none in a browser. */
export function detectHost(): Host | null {
  return hostPresent() ? tauriHost() : null;
}

export const HostContext = createContext<Host | null>(null);

/** The host the page runs under, or null in the browser: the local parts render only with one. */
export function useHost(): Host | null {
  return useContext(HostContext);
}
